/**
 * LineageOverlay — the prerequisite connectors drawn over the poster grid.
 *
 * A decorative SVG (aria-hidden) that measures each tile's live position and
 * draws a bezier from a prerequisite's bottom edge to its dependent's top
 * edge. Lives *inside* the grid element (which is `position: relative`), so
 * coordinates are grid-relative and the lines scroll with the tiles — no
 * scroll-offset math. Re-measures on resize, font load, and edge changes.
 *
 * When `litNodeIds` is set (a tile is hovered/focused) the edges on that
 * lineage brighten and the rest recede. Nothing is ever hidden — the lines
 * are lineage, not gates.
 */

import { type ReactNode, useLayoutEffect, useState } from "react";
import type { LineageEdge } from "./lineage.js";

interface MeasuredEdge extends LineageEdge {
  d: string;
}

interface LineageOverlayProps {
  /** The `position: relative` grid element the tiles live in. */
  gridRef: { current: HTMLElement | null };
  edges: LineageEdge[];
  /** Node ids on the active lineage, or null when nothing is hovered/focused. */
  litNodeIds: Set<string> | null;
  colorForTrack: (track: string) => string;
}

export function LineageOverlay({
  gridRef,
  edges,
  litNodeIds,
  colorForTrack,
}: LineageOverlayProps): ReactNode {
  const [paths, setPaths] = useState<MeasuredEdge[]>([]);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    let cancelled = false;
    let raf = 0;

    // Returns whether every edge resolved to a live pair of tiles. A tile
    // can still be settling into its final layout right after this effect's
    // component (re)mounts — e.g. returning to /learn from another route,
    // a webfont swap, or a backgrounded tab catching up — so a measurement
    // that comes up short doesn't mean the edge is gone, just not ready yet.
    const measure = (): boolean => {
      const gr = grid.getBoundingClientRect();
      const next: MeasuredEdge[] = [];
      for (const edge of edges) {
        const fromEl = grid.querySelector<HTMLElement>(
          `[data-node-id="${edge.from}"]`,
        );
        const toEl = grid.querySelector<HTMLElement>(
          `[data-node-id="${edge.to}"]`,
        );
        if (!fromEl || !toEl) continue;
        const fr = fromEl.getBoundingClientRect();
        const tr = toEl.getBoundingClientRect();
        const fx = fr.left - gr.left + fr.width / 2;
        const fy = fr.bottom - gr.top;
        const tx = tr.left - gr.left + tr.width / 2;
        const ty = tr.top - gr.top;
        const bend = Math.max(24, (ty - fy) * 0.5);
        next.push({
          ...edge,
          d: `M${fx},${fy} C${fx},${fy + bend} ${tx},${ty - bend} ${tx},${ty}`,
        });
      }
      setPaths(next);
      setSize({ w: grid.offsetWidth, h: grid.offsetHeight });
      return next.length === edges.length;
    };

    const schedule = (): void => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!cancelled) measure();
      });
    };

    // Retry across a handful of frames until every edge resolves, instead
    // of trusting a single measurement taken the instant this effect runs.
    let settleAttempts = 0;
    const settleUntilResolved = (): void => {
      if (cancelled) return;
      const resolved = measure();
      settleAttempts += 1;
      if (!resolved && settleAttempts < 10) {
        raf = requestAnimationFrame(settleUntilResolved);
      }
    };

    settleUntilResolved();
    const observer = new ResizeObserver(schedule);
    observer.observe(grid);
    window.addEventListener("resize", schedule);
    // Web fonts shift text metrics after load; re-measure once ready.
    document.fonts?.ready
      .then(() => {
        if (!cancelled) measure();
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [edges, gridRef]);

  return (
    <svg
      aria-hidden="true"
      width={size.w}
      height={size.h}
      viewBox={`0 0 ${size.w} ${size.h}`}
      className="pointer-events-none absolute left-0 top-0"
      style={{ zIndex: 1 }}
    >
      {paths.map((path) => {
        const lit = litNodeIds
          ? litNodeIds.has(path.from) && litNodeIds.has(path.to)
          : null;
        const opacity = lit === null ? 0.4 : lit ? 0.95 : 0.06;
        return (
          <path
            key={`${path.from}->${path.to}`}
            d={path.d}
            fill="none"
            stroke={colorForTrack(path.fromTrack)}
            strokeWidth={lit ? 2.25 : 1.75}
            strokeLinecap="round"
            className="motion-reduce:transition-none"
            style={{ opacity, transition: "opacity .15s ease, stroke-width .15s ease" }}
          />
        );
      })}
    </svg>
  );
}
